import {
  Badge,
  Box,
  BoxProps,
  Flex,
  HStack,
  TableHeadProps,
  TableRowProps,
  Text,
} from "@chakra-ui/react";
import { useEffect, useState } from "react";
import { useAppDispatch, useAppSelector } from "store";
import {
  useGetUserLoyaltyPointsListQuery,
  useGetUserProfileQuery,
} from "store/services/user";

import { logInAction } from "@/store/slices";
import { DataTable } from "@picovico/ui";
import { FaCheckCircle } from "react-icons/fa";
import { IoMdWarning } from "react-icons/io";

interface MembershipTabProps {
  tableContainerProps?: BoxProps;
  tHeadProps?: TableHeadProps;
  tRowProps?: TableRowProps;
}

export interface MembershipPointsTabProps {
  membershipTabProps?: MembershipTabProps;
}

function MembershipPoints({ membershipTabProps }: MembershipPointsTabProps) {
  const defaultColor = useAppSelector(
    // @ts-expect-error ignore this since it is used for optional chaining
    (state) => state.config.site_theme.colors.background,
  );
  const dispatch = useAppDispatch();
  const member = useAppSelector((state) => state.user.user?.member);
  const [page, setPage] = useState(1);
  const { data: userData } = useGetUserProfileQuery({});
  const { data, isLoading } = useGetUserLoyaltyPointsListQuery({
    page_size: 10,
    page,
  });

  useEffect(() => {
    userData?.data && dispatch(logInAction(userData?.data));
  }, [dispatch, userData?.data]);

  const { tableContainerProps, tHeadProps, tRowProps } =
    membershipTabProps || {};

  const columnSchema = [
    {
      id: "S.N",
      accessorFn: (v: unknown, i: number) => i + 1,
      header: "S.N",
    },
    {
      id: "movie",
      accessorKey: "movie",
      header: "Movie",
    },
    {
      id: "cinema_hall",
      accessorKey: "cinema_hall",
      header: "Cinema Hall",
    },
    {
      id: "show_datetime",
      accessorFn: (v: { show_datetime: string }) =>
        new Date(v.show_datetime).toLocaleDateString(),
      header: "Show Date",
    },
    {
      id: "points",
      accessorKey: "points",
      header: "Points",
    },
    {
      id: "amount_spent",
      accessorKey: "amount_spent",
      header: "Amount Spent",
    },
    {
      id: "created_at",
      accessorFn: (v: { created_at: string }) =>
        new Date(v.created_at).toLocaleDateString(),
      header: "Created At",
    },
    {
      id: "remarks",
      accessorKey: "remarks",
      header: "Remarks",
    },
  ];

  return (
    <Box
      overflowX="auto"
      w={{
        base: "xs",
        md: "md",
        xl: "full",
      }}
      maxW="100%"
      p={{ base: 0, md: 4 }}
      {...tableContainerProps}
    >
      <HStack
        spacing={{ base: 2, md: 4 }}
        flexDirection={{ base: "column", md: "row" }}
        alignItems={{ base: "flex-start", md: "center" }}
        justifyContent={{ base: "flex-start", md: "space-between" }}
        mb={4}
        borderBottom={"1px solid"}
        borderColor="surface.200"
        pb={4}
        w="full"
        maxW="100%"
        overflowX="auto"
      >
        <Flex
          justifyContent="space-between"
          w={{ base: "full", md: "auto" }}
          mb={{ base: 2, md: 0 }}
        >
          <Text fontWeight="medium" fontSize={{ base: "sm", md: "md" }}>
            Code:
          </Text>
          <Text fontSize={{ base: "sm", md: "md" }}>{member?.code || ""}</Text>
        </Flex>

        <Flex
          justifyContent="space-between"
          w={{ base: "full", md: "auto" }}
          mb={{ base: 2, md: 0 }}
        >
          <Text fontWeight="medium" fontSize={{ base: "sm", md: "md" }}>
            Available Points:
          </Text>
          <Text fontSize={{ base: "sm", md: "md" }}>
            {member?.available_points}
          </Text>
        </Flex>

        <Flex
          justifyContent="space-between"
          alignItems="center"
          w={{ base: "full", md: "auto" }}
        >
          <Text fontWeight="medium" fontSize={{ base: "sm", md: "md" }}>
            Verified:
          </Text>
          {member?.is_verified ? (
            <Badge
              colorScheme="green"
              px={{ base: 1, md: 2 }}
              py={1}
              borderRadius="md"
              display="flex"
              alignItems="center"
              fontSize={{ base: "xs", md: "sm" }}
            >
              <FaCheckCircle /> Verified
            </Badge>
          ) : (
            <Badge
              colorScheme="red"
              px={{ base: 1, md: 2 }}
              py={1}
              borderRadius="md"
              display="flex"
              alignItems="center"
              fontSize={{ base: "xs", md: "sm" }}
            >
              <IoMdWarning /> Not Verified
            </Badge>
          )}
        </Flex>
      </HStack>

      <DataTable
        columnSchema={columnSchema}
        data={data?.data?.records || []}
        isLoading={isLoading}
        activePage={page}
        handlePageChange={(p) => setPage(p)}
        pageCount={data?.data?.total_pages || 0}
        tHeadProps={{ ...tHeadProps }}
        tRowProps={{
          background: defaultColor.nav,
          _hover: { backgroundColor: defaultColor.navHover ?? "surface.100" },
          ...tRowProps,
        }}
      />
    </Box>
  );
}

export default MembershipPoints;
